import React, { useState } from 'react';
import { Card, Form, Button, Row, Col, Alert, InputGroup, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronLeft, faPlus, faTimes, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import StepHeader from './StepHeader';
import '../common.css';

// Target region options
const regionOptions = [
  { id: "north-america", label: "North America" },
  { id: "south-america", label: "South America" },
  { id: "europe", label: "Europe" },
  { id: "asia", label: "Asia" },
  { id: "australia-oceania", label: "Australia/Oceania" },
  { id: "africa", label: "Africa" },
  { id: "middle-east", label: "Middle East" }
];

// Season preference options
const seasonOptions = [
  { id: "spring", label: "Spring" },
  { id: "summer", label: "Summer" },
  { id: "fall", label: "Fall" },
  { id: "winter", label: "Winter" },
  { id: "year-round", label: "Year-round" }
];

const ScopeTimeline = ({ formData, onInputChange, onNext, onPrev, error }) => {
  const [newCity, setNewCity] = useState('');
  const [avoidRegion, setAvoidRegion] = useState('');
  const [validationError, setValidationError] = useState('');
  
  // Handle checkbox changes for multi-select options
  const handleMultiSelectChange = (field, value, isChecked) => {
    const currentValues = formData[field] || [];
    let newValues;
    
    if (isChecked) {
      newValues = [...currentValues, value];
    } else {
      newValues = currentValues.filter(item => item !== value);
    }
    
    onInputChange({ [field]: newValues });
    
    // Clear validation error if the required field is now filled
    if (field === 'targetRegions' && newValues.length > 0) {
      setValidationError('');
    }
  };

  // Handle input changes for text fields
  const handleInputChange = (field, value) => {
    onInputChange({ [field]: value });
    
    // Clear validation error if the required field is now filled
    if (field === 'tourDuration' && value) {
      setValidationError('');
    }
  };

  // Check if a value is selected in a multi-select list
  const isSelected = (field, value) => {
    return (formData[field] || []).includes(value);
  };
  
  // Add a specific city to the list
  const addCity = () => {
    if (!newCity.trim()) return;
    
    const currentCities = formData.specificCities || [];
    if (!currentCities.includes(newCity.trim())) {
      onInputChange({ 
        specificCities: [...currentCities, newCity.trim()] 
      });
    }
    
    setNewCity('');
  };
  
  // Remove a city from the list
  const removeCity = (city) => {
    const currentCities = formData.specificCities || [];
    onInputChange({ 
      specificCities: currentCities.filter(c => c !== city) 
    });
  };
  
  // Add a region to avoid
  const addAvoidRegion = () => {
    if (!avoidRegion.trim()) return;
    
    const currentRegionsToAvoid = formData.regionsToAvoid || [];
    if (!currentRegionsToAvoid.includes(avoidRegion.trim())) {
      onInputChange({ 
        regionsToAvoid: [...currentRegionsToAvoid, avoidRegion.trim()] 
      });
    }
    
    setAvoidRegion('');
  };
  
  // Remove a region to avoid
  const removeAvoidRegion = (region) => {
    const currentRegionsToAvoid = formData.regionsToAvoid || [];
    onInputChange({ 
      regionsToAvoid: currentRegionsToAvoid.filter(r => r !== region) 
    });
  };
  
  // Format specific dates based on local format
  const formatInputDate = (date) => {
    if (!date) return '';
    
    // If it's already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    
    try {
      // Convert to YYYY-MM-DD format for input field
      const d = new Date(date);
      return d.toISOString().split('T')[0];
    } catch (e) {
      return date;
    }
  };
  
  // Validate form before proceeding to next step
  const validateForm = () => {
    // Check if required fields are filled
    if (!formData.tourDuration) {
      setValidationError('Please specify the tour duration');
      return false;
    }
    
    if (!formData.targetRegions || formData.targetRegions.length === 0) {
      setValidationError('Please select at least one target region');
      return false;
    }
    
    return true;
  };
  
  // Handle next button click
  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  return (
    <Card className="dark-main-card text-white mb-4">
      <Card.Header className="dark-card-header border-bottom">
        <StepHeader title="Plan Your Timeline & Geographic Strategy" />
      </Card.Header>
      <Card.Body className="p-4">
        {error && <Alert variant="danger" className="dark-alert mb-4">{error}</Alert>}
        {validationError && (
          <Alert variant="warning" className="dark-alert mb-4">
            <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
            {validationError}
          </Alert>
        )}
        
        <Form>
          {/* Tour Duration Section */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Tour Duration</h5>
            
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-white">Tour length <span className="text-danger">*</span></Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      min="1"
                      className="dark-input"
                      value={formData.tourDuration || ''}
                      onChange={(e) => handleInputChange('tourDuration', e.target.value)}
                      placeholder="Duration"
                      required
                    />
                    <Form.Select
                      className="dark-input"
                      value={formData.tourDurationUnit || 'days'}
                      onChange={(e) => handleInputChange('tourDurationUnit', e.target.value)}
                    >
                      <option value="days">Days</option>
                      <option value="weeks">Weeks</option>
                      <option value="months">Months</option>
                    </Form.Select>
                  </InputGroup>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-white">Events per week</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="7"
                    className="dark-input"
                    value={formData.eventsPerWeek || ''}
                    onChange={(e) => handleInputChange('eventsPerWeek', e.target.value)}
                    placeholder="Number of events"
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-white">Preferred start date</Form.Label>
                  <Form.Control
                    type="date"
                    className="dark-input"
                    value={formatInputDate(formData.startDate)}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="text-white">Preferred end date</Form.Label>
                  <Form.Control
                    type="date"
                    className="dark-input"
                    value={formatInputDate(formData.endDate)}
                    onChange={(e) => handleInputChange('endDate', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">
                Date flexibility ({formData.dateFlexibility || 0}%)
              </Form.Label>
              <Form.Range
                min="0"
                max="100"
                step="5"
                value={formData.dateFlexibility || 0}
                onChange={(e) => handleInputChange('dateFlexibility', e.target.value)}
                className="dark-range"
              />
              <div className="d-flex justify-content-between">
                <small className="text-muted">Fixed dates</small>
                <small className="text-muted">Completely flexible</small>
              </div>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Season preferences</Form.Label>
              <Row>
                {seasonOptions.map(season => (
                  <Col md={3} key={season.id} className="mb-2">
                    <Form.Check
                      type="checkbox"
                      id={`season-${season.id}`}
                      className="dark-checkbox"
                      label={season.label}
                      checked={isSelected('seasonPreferences', season.id)}
                      onChange={(e) => handleMultiSelectChange('seasonPreferences', season.id, e.target.checked)}
                    />
                  </Col>
                ))}
              </Row>
            </Form.Group>
          </div>
          
          {/* Geographic Scope Section */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Geographic Scope</h5>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Target regions <span className="text-danger">*</span></Form.Label>
              <Row>
                {regionOptions.map(region => (
                  <Col md={4} key={region.id} className="mb-2">
                    <Form.Check
                      type="checkbox"
                      id={`region-${region.id}`}
                      className="dark-checkbox"
                      label={region.label}
                      checked={isSelected('targetRegions', region.id)}
                      onChange={(e) => handleMultiSelectChange('targetRegions', region.id, e.target.checked)}
                    />
                  </Col>
                ))}
              </Row>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Required cities to include</Form.Label>
              <InputGroup className="mb-2">
                <Form.Control
                  type="text"
                  className="dark-input"
                  placeholder="Enter city name"
                  value={newCity}
                  onChange={(e) => setNewCity(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCity())}
                />
                <Button 
                  variant="outline-light" 
                  onClick={addCity}
                  disabled={!newCity.trim()}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </Button>
              </InputGroup>
              
              <div className="mt-2">
                {(formData.specificCities || []).map((city, index) => (
                  <Badge 
                    bg="primary" 
                    key={index} 
                    className="me-2 mb-2"
                    style={{ background: 'linear-gradient(90deg, rgba(145, 105, 247, 0.8) 0%, rgba(113, 71, 219, 0.8) 100%)' }}
                  >
                    {city}
                    <span 
                      className="ms-2" 
                      style={{ cursor: 'pointer' }}
                      onClick={() => removeCity(city)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </span>
                  </Badge>
                ))}
                {!(formData.specificCities || []).length && 
                  <small className="text-muted">No required cities added</small>
                }
              </div>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Regions or cities to avoid</Form.Label>
              <InputGroup className="mb-2">
                <Form.Control
                  type="text"
                  className="dark-input"
                  placeholder="Enter region or city to avoid"
                  value={avoidRegion}
                  onChange={(e) => setAvoidRegion(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAvoidRegion())}
                />
                <Button 
                  variant="outline-light" 
                  onClick={addAvoidRegion}
                  disabled={!avoidRegion.trim()}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </Button>
              </InputGroup>
              
              <div className="mt-2">
                {(formData.regionsToAvoid || []).map((region, index) => (
                  <Badge 
                    bg="danger" 
                    key={index} 
                    className="me-2 mb-2"
                    style={{ background: 'linear-gradient(90deg, rgba(145, 105, 247, 0.8) 0%, rgba(113, 71, 219, 0.8) 100%)' }}
                  >
                    {region}
                    <span 
                      className="ms-2" 
                      style={{ cursor: 'pointer' }}
                      onClick={() => removeAvoidRegion(region)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </span>
                  </Badge>
                ))}
                {!(formData.regionsToAvoid || []).length && 
                  <small className="text-muted">No exclusions added</small>
                }
              </div>
            </Form.Group>
          </div>
          
          {/* Routing Preferences */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Routing Preferences</h5>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Routing strategy</Form.Label>
              <Form.Select
                className="dark-input"
                value={formData.routingStrategy || ''}
                onChange={(e) => handleInputChange('routingStrategy', e.target.value)}
              >
                <option value="">Select a routing strategy...</option>
                <option value="sequential">Sequential (minimize travel)</option>
                <option value="market-driven">Market-driven (prioritize best markets)</option>
                <option value="date-driven">Date-driven (fixed dates at specific venues)</option>
                <option value="balanced">Balanced approach</option>
              </Form.Select>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label className="text-white">Travel day considerations</Form.Label>
              <Form.Select
                className="dark-input"
                value={formData.travelDayConsideration || ''}
                onChange={(e) => handleInputChange('travelDayConsideration', e.target.value)}
              >
                <option value="">Select preference...</option>
                <option value="avoid-consecutive">Avoid consecutive show days with long travel</option>
                <option value="dedicated-travel">Schedule dedicated travel days</option>
                <option value="travel-show">Can travel and perform on same day for short distances</option>
                <option value="no-preference">No specific preference</option>
              </Form.Select>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="clustered-routing"
                className="dark-checkbox"
                label="Cluster shows geographically before moving to next region"
                checked={formData.clusteredRouting || false}
                onChange={(e) => handleInputChange('clusteredRouting', e.target.checked)}
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="prefer-weekends"
                className="dark-checkbox"
                label="Prioritize weekend dates for key markets"
                checked={formData.prioritizeWeekends || false}
                onChange={(e) => handleInputChange('prioritizeWeekends', e.target.checked)}
              />
            </Form.Group>
          </div>
          
          {/* Off Days & Breaks */}
          <div className="mb-5">
            <h5 className="text-white mb-3">Off Days & Breaks</h5>
            
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-white">Minimum days off per week</Form.Label>
                  <Form.Select
                    className="dark-input"
                    value={formData.daysOffPerWeek || ''}
                    onChange={(e) => handleInputChange('daysOffPerWeek', e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="1">1 day</option>
                    <option value="2">2 days</option>
                    <option value="3">3 days</option>
                    <option value="4">4 days</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="text-white">Maximum consecutive show days</Form.Label>
                  <Form.Select
                    className="dark-input"
                    value={formData.maxConsecutiveShows || ''}
                    onChange={(e) => handleInputChange('maxConsecutiveShows', e.target.value)}
                  >
                    <option value="">Select...</option>
                    <option value="2">2 days</option>
                    <option value="3">3 days</option>
                    <option value="4">4 days</option>
                    <option value="5">5 days</option>
                    <option value="6">6 days</option>
                    <option value="7">7+ days</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="extended-breaks"
                className="dark-checkbox"
                label="Include extended breaks during longer tours"
                checked={formData.extendedBreaks || false}
                onChange={(e) => handleInputChange('extendedBreaks', e.target.checked)}
              />
              
              {formData.extendedBreaks && (
                <InputGroup className="mt-2" style={{ width: '60%', marginLeft: '1.5rem' }}>
                  <Form.Control
                    type="number"
                    min="1"
                    className="dark-input"
                    placeholder="Break length (days)"
                    value={formData.extendedBreakLength || ''}
                    onChange={(e) => handleInputChange('extendedBreakLength', e.target.value)}
                  />
                  <InputGroup.Text className="dark-input-group-text">days</InputGroup.Text>
                  <Form.Control
                    type="number"
                    min="1"
                    className="dark-input"
                    placeholder="Every"
                    value={formData.extendedBreakFrequency || ''}
                    onChange={(e) => handleInputChange('extendedBreakFrequency', e.target.value)}
                  />
                  <InputGroup.Text className="dark-input-group-text">weeks</InputGroup.Text>
                </InputGroup>
              )}
            </Form.Group>
          </div>
          
          {/* Additional Notes */}
          <Form.Group className="mb-4">
            <Form.Label className="text-white">Additional Timeline Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              className="dark-input"
              placeholder="Any other timeline or routing considerations"
              value={formData.timelineNotes || ''}
              onChange={(e) => handleInputChange('timelineNotes', e.target.value)}
            />
          </Form.Group>
          
          <div className="d-flex justify-content-between">
            <Button
              variant="outline-light"
              className="dark-outline-button"
              onClick={onPrev}
            >
              <FontAwesomeIcon icon={faChevronLeft} className="me-2" />
              Back
            </Button>
            <Button
              className="dark-primary-button"
              onClick={handleNext}
            >
              Continue
              <FontAwesomeIcon icon={faChevronRight} className="ms-2" />
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
  );
};

export default ScopeTimeline;